import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { jobsQueryKey, listJobs, type PaymentMethod } from '@/lib/api/jobs';
import { listPaymentMethods, paymentMethodsQueryKey } from '@/lib/api/payment-methods';

import { defaultDueDate, toOptionalAmount } from './helpers';
import { PaymentMethodsField } from './payment-methods-field';
import { addInvoiceSchema, type AddInvoiceInput } from './schema';
import type { NewInvoiceSubmission } from './types';

interface Props {
  businessId: string;
  // When provided, the job is fixed (opened from a job) and the picker is hidden.
  jobId?: string;
  jobTitle?: string;
  // The fixed job's preferred payment method, when `jobId` is provided — used
  // to default the offered-methods selection below.
  jobDefaultPaymentMethod?: PaymentMethod | null;
  // The fixed job's specific preferred account (e.g. one of several Venmo
  // accounts), when set — preferred over `jobDefaultPaymentMethod` below.
  jobDefaultPaymentMethodId?: string | null;
  // Pre-fills the Total field, e.g. with a specific visit's billed amount.
  initialTotalAmount?: number;
  onSave: (submission: NewInvoiceSubmission) => void;
  onClose: () => void;
  isSaving: boolean;
}

const DEFAULT_VALUES: AddInvoiceInput = {
  jobId: '',
  totalAmount: '',
  subtotal: '',
  tax: '',
  discount: '',
  dueDate: '',
  showTaxBreakdown: false,
  offerAllMethods: true,
  selectedMethodIds: [],
};

export function AddInvoiceModal({
  businessId,
  jobId,
  jobTitle,
  jobDefaultPaymentMethod,
  jobDefaultPaymentMethodId,
  initialTotalAmount,
  onSave,
  onClose,
  isSaving,
}: Props) {
  const { t } = useLanguage();

  const form = useForm<AddInvoiceInput>({
    defaultValues: {
      ...DEFAULT_VALUES,
      jobId: jobId ?? '',
      dueDate: defaultDueDate(),
      totalAmount: initialTotalAmount != null ? String(initialTotalAmount) : '',
    },
    resolver: zodResolver(addInvoiceSchema),
  });

  const jobsQuery = useQuery({
    queryKey: jobsQueryKey(businessId, {}),
    queryFn: () => listJobs(businessId),
    enabled: !jobId,
  });

  const paymentMethodsQuery = useQuery({
    queryKey: paymentMethodsQueryKey(businessId),
    queryFn: () => listPaymentMethods(businessId),
  });

  const jobs = jobsQuery.data ?? [];
  const showTaxBreakdown = form.watch('showTaxBreakdown');
  const offerAllMethods = form.watch('offerAllMethods');
  const selectedMethodIds = form.watch('selectedMethodIds');
  const selectedJobId = form.watch('jobId');

  // The selected job's preferred payment method: from the fixed-job props, or
  // looked up from the already-fetched job list when picked via the dropdown.
  const selectedJob = jobId ? null : jobs.find((j) => j.id === selectedJobId);
  const preferredPaymentMethodId = jobId
    ? jobDefaultPaymentMethodId
    : (selectedJob?.default_payment_method_id ?? null);
  const preferredPaymentMethod = jobId
    ? jobDefaultPaymentMethod
    : (selectedJob?.default_payment_method ?? null);

  // Defaults "offer this invoice's payment methods" to just the job's
  // preferred one (when the business has it actively configured), instead of
  // the usual "offer all" — unless the owner has already touched the field
  // themselves, or re-derives it if they switch the selected job first.
  // Prefers the job's specific account id (e.g. one of several Venmo
  // accounts) over a plain type match.
  useEffect(() => {
    if (
      form.formState.dirtyFields.offerAllMethods ||
      form.formState.dirtyFields.selectedMethodIds
    ) {
      return;
    }
    if (!paymentMethodsQuery.data) return;

    const match = preferredPaymentMethodId
      ? paymentMethodsQuery.data.find(
          (m) => m.is_active && m.id === preferredPaymentMethodId,
        )
      : preferredPaymentMethod
        ? paymentMethodsQuery.data.find(
            (m) => m.is_active && m.type === preferredPaymentMethod,
          )
        : undefined;
    if (!match) return;

    form.setValue('offerAllMethods', false);
    form.setValue('selectedMethodIds', [match.id]);
  }, [preferredPaymentMethodId, preferredPaymentMethod, paymentMethodsQuery.data, form]);

  function handleSubmit(data: AddInvoiceInput) {
    onSave({
      jobId: data.jobId,
      total_amount: Number(data.totalAmount),
      payment_method_ids: data.offerAllMethods ? [] : data.selectedMethodIds,
      subtotal: data.showTaxBreakdown ? toOptionalAmount(data.subtotal) : undefined,
      tax: data.showTaxBreakdown ? toOptionalAmount(data.tax) : undefined,
      discount: data.showTaxBreakdown ? toOptionalAmount(data.discount) : undefined,
      due_date: data.dueDate || undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">{t.addInvoiceTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={form.handleSubmit(handleSubmit)} className="overflow-y-auto flex-1">
          <div className="px-6 py-5 flex flex-col gap-4">
            {jobId ? (
              <div className="flex flex-col gap-1.5">
                <Label>{t.invoiceJob}</Label>
                <p className="text-sm">{jobTitle}</p>
              </div>
            ) : (
              <Controller
                control={form.control}
                name="jobId"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="invoice-job">{t.invoiceJob}</Label>
                    <select
                      {...field}
                      id="invoice-job"
                      data-testid="invoice-job-select"
                      aria-invalid={fieldState.invalid}
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="" disabled>
                        {t.invoiceJobPlaceholder}
                      </option>
                      {jobs.map((job) => (
                        <option key={job.id} value={job.id}>
                          {job.title}
                        </option>
                      ))}
                    </select>
                    {!jobsQuery.isPending && jobs.length === 0 && (
                      <p className="text-xs text-muted-foreground">{t.invoiceNoJobs}</p>
                    )}
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showTaxBreakdown}
                onChange={(e) => form.setValue('showTaxBreakdown', e.target.checked, { shouldDirty: true })}
                data-testid="invoice-show-tax-breakdown"
              />
              {t.invoiceShowTaxBreakdown}
            </label>

            <div className={showTaxBreakdown ? 'grid grid-cols-2 gap-3' : ''}>
              {showTaxBreakdown && (
                <>
                  <Controller
                    control={form.control}
                    name="subtotal"
                    render={({ field, fieldState }) => (
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="invoice-subtotal">{t.invoiceSubtotal}</Label>
                        <input
                          {...field}
                          id="invoice-subtotal"
                          inputMode="decimal"
                          placeholder="0.00"
                          data-testid="invoice-subtotal-input"
                          className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        {fieldState.error && (
                          <p className="text-xs text-destructive">{fieldState.error.message}</p>
                        )}
                      </div>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="tax"
                    render={({ field, fieldState }) => (
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="invoice-tax">{t.invoiceTax}</Label>
                        <input
                          {...field}
                          id="invoice-tax"
                          inputMode="decimal"
                          placeholder="0.00"
                          data-testid="invoice-tax-input"
                          className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        {fieldState.error && (
                          <p className="text-xs text-destructive">{fieldState.error.message}</p>
                        )}
                      </div>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="discount"
                    render={({ field, fieldState }) => (
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="invoice-discount">{t.invoiceDiscount}</Label>
                        <input
                          {...field}
                          id="invoice-discount"
                          inputMode="decimal"
                          placeholder="0.00"
                          data-testid="invoice-discount-input"
                          className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        {fieldState.error && (
                          <p className="text-xs text-destructive">{fieldState.error.message}</p>
                        )}
                      </div>
                    )}
                  />
                </>
              )}
              <Controller
                control={form.control}
                name="totalAmount"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="invoice-total">{t.invoiceTotal}</Label>
                    <input
                      {...field}
                      id="invoice-total"
                      inputMode="decimal"
                      placeholder="0.00"
                      data-testid="invoice-total-input"
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />
            </div>

            <Controller
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invoice-due-date">{t.invoiceDueDate}</Label>
                  <input
                    {...field}
                    type="date"
                    id="invoice-due-date"
                    data-testid="invoice-due-date-input"
                    className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}
            />

            <PaymentMethodsField
              businessId={businessId}
              offerAll={offerAllMethods}
              selectedIds={selectedMethodIds}
              onToggleOfferAll={(v) => form.setValue('offerAllMethods', v, { shouldDirty: true })}
              onToggleMethod={(id) =>
                form.setValue(
                  'selectedMethodIds',
                  selectedMethodIds.includes(id)
                    ? selectedMethodIds.filter((existing) => existing !== id)
                    : [...selectedMethodIds, id],
                  { shouldDirty: true },
                )
              }
              error={form.formState.errors.selectedMethodIds?.message}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t.cancel}
            </Button>
            <Button type="submit" size="sm" disabled={isSaving} data-testid="save-invoice">
              {t.saveInvoice}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
